/**
 * Patient Study Detail Page
 *
 * Shows study details and report for patients
 */

import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  FileImage,
  FileText,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Building,
  Calendar,
  Printer,
  Download,
  Image as ImageIcon,
  Paperclip,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Maximize2,
} from 'lucide-react'

import {
  getMyStudyById,
  getMyStudyReportConAddenda,
  type AddendumDelPaciente,
  getMyStudyAttachments,
  getMyStudyInstances,
  getAttachmentDownloadUrl,
  type PatientAttachment,
  type PatientStudyInstance,
  descargarMiEstudio,
  guardarArchivo,
  type FormatoDeDescarga,
} from '@/application/patient-portal/patient-portal.service'
import { patientTokenStorage } from '@/lib/api'
import { toast } from '@/lib/toast'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { APP_ROUTES, MODALITY_LABELS, type Modality } from '@/config/constants'
import { AuthenticatedImage } from '@/presentation/components/ui/AuthenticatedImage'

// =============================================================================
// Patient Status Badge (Simplified for Patients)
// =============================================================================

/**
 * For patients, we show simplified status:
 * - "Informe Disponible" (green) - Study has a final report ready
 * - "En Proceso" (yellow) - Study is being processed (no final report yet)
 *
 * Patients should NOT see internal workflow states like "En Lectura", "Devuelto", etc.
 */
function PatientStatusBadge({ hasReport }: { hasReport: boolean }) {
  if (hasReport) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
        <CheckCircle className="h-4 w-4" />
        Informe Disponible
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
      <Clock className="h-4 w-4" />
      En Proceso
    </span>
  )
}

// =============================================================================
// Markdown Rendering Helper
// =============================================================================

/**
 * Simple markdown renderer for report content
 * Converts **bold** to <strong> tags and preserves line breaks
 */
function renderMarkdownText(text: string): React.ReactElement[] {
  // Split by lines first to preserve line breaks
  const lines = text.split('\n')

  return lines.map((line, lineIndex) => {
    // Convert **bold** to strong tags
    const parts: (string | React.ReactElement)[] = []
    const boldRegex = /\*\*(.+?)\*\*/g
    let lastIndex = 0
    let match
    let partIndex = 0

    while ((match = boldRegex.exec(line)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index))
      }
      // Add the bold text
      parts.push(<strong key={`bold-${lineIndex}-${partIndex++}`}>{match[1]}</strong>)
      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex))
    }

    // If line is empty, just return a break
    if (parts.length === 0) {
      return <br key={`line-${lineIndex}`} />
    }

    return (
      <span key={`line-${lineIndex}`}>
        {parts}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    )
  })
}

// =============================================================================
// Report Section
// =============================================================================

function ReportSection({ studyId, hasReport }: { studyId: string; hasReport: boolean }) {
  const { data: informe, isLoading, error } = useQuery({
    queryKey: ['patient-study-report', studyId],
    queryFn: () => getMyStudyReportConAddenda(studyId),
    enabled: hasReport,
  })

  if (!hasReport) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-400" />
            Informe Médico
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Informe en proceso</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              El informe de este estudio aún está siendo elaborado por nuestros especialistas.
              Recibirá una notificación cuando esté disponible.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-teal-600 dark:text-teal-400" />
            <span className="text-gray-500 dark:text-gray-400">Cargando informe...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error || !informe) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Informe no disponible</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              El informe aún no está listo para consulta.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const handlePrint = () => {
    window.print()
  }

  const { report, addenda } = informe

  return (
    <Card className="print:shadow-none print:border-0">
      <CardHeader className="flex flex-row items-center justify-between print:hidden">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            Informe Médico
          </CardTitle>
          <CardDescription>
            {report.signedAt && (
              <>Firmado el {format(new Date(report.signedAt), "d 'de' MMMM, yyyy", { locale: es })}</>
            )}
          </CardDescription>
        </div>
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Imprimir
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Findings */}
        <div>
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Hallazgos</h4>
          <div className="bg-gray-50 dark:bg-gray-950/40 rounded-lg p-4 text-gray-700 dark:text-gray-300 print:bg-transparent print:p-0">
            {renderMarkdownText(report.findings)}
          </div>
        </div>

        {/* Impression */}
        {report.impression && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Impresión Diagnóstica</h4>
            <div className="bg-gray-50 dark:bg-gray-950/40 rounded-lg p-4 text-gray-700 dark:text-gray-300 print:bg-transparent print:p-0">
              {renderMarkdownText(report.impression)}
            </div>
          </div>
        )}

        {/* Conclusion */}
        {report.conclusion && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Conclusión</h4>
            <div className="bg-teal-50 dark:bg-teal-950/40 rounded-lg p-4 text-gray-700 dark:text-gray-300 border border-teal-200 dark:border-teal-800 print:bg-transparent print:border-0 print:p-0">
              {renderMarkdownText(report.conclusion)}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {report.recommendations && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Recomendaciones</h4>
            <div className="bg-yellow-50 dark:bg-yellow-950/40 rounded-lg p-4 text-gray-700 dark:text-gray-300 border border-yellow-200 dark:border-yellow-800 print:bg-transparent print:border-0 print:p-0">
              {renderMarkdownText(report.recommendations)}
            </div>
          </div>
        )}

        {/* Notas que el médico agregó después de firmar.
            Van al final y separadas a propósito: lo que se firmó aquel día y
            lo que se añadió después no se mezclan nunca. Si se mezclaran, el
            informe parecería haber dicho desde el principio algo que no dijo. */}
        {addenda.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              Notas agregadas por su médico
            </h4>
            <div className="space-y-4">
              {addenda.map((nota: AddendumDelPaciente) => (
                <div
                  key={nota.id}
                  className="border-l-2 border-teal-400 dark:border-teal-600 pl-4 py-1"
                >
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {format(new Date(nota.createdAt), "d 'de' MMMM, yyyy", { locale: es })}
                    {nota.firmante?.nombre ? ` · ${nota.firmante.nombre}` : ''}
                  </p>
                  <div className="mt-1 text-gray-700 dark:text-gray-300">
                    {renderMarkdownText(nota.content)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Attachments Section
// =============================================================================

const ATTACHMENT_TYPE_LABELS: Record<string, string> = {
  CLINICAL_HISTORY: 'Historia Clínica',
  MEDICAL_ORDER: 'Orden Médica',
  PREVIOUS_RESULTS: 'Resultados Anteriores',
  ADDITIONAL_IMAGES: 'Imágenes Adicionales',
  OTHER: 'Otro',
}

function AttachmentsSection({ studyId }: { studyId: string }) {
  const { data: attachments, isLoading } = useQuery({
    queryKey: ['patient-study-attachments', studyId],
    queryFn: () => getMyStudyAttachments(studyId),
  })

  const [downloading, setDownloading] = useState<string | null>(null)

  const handleDownload = async (attachment: PatientAttachment) => {
    setDownloading(attachment.id)
    try {
      const { downloadUrl } = await getAttachmentDownloadUrl(studyId, attachment.id)
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Error downloading attachment:', error)
    } finally {
      setDownloading(null)
    }
  }

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-teal-600 dark:text-teal-400" />
            <span className="text-gray-500 dark:text-gray-400">Cargando documentos...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          Documentos Adjuntos
        </CardTitle>
        <CardDescription>
          Documentos relacionados con su estudio
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-teal-300 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{attachment.fileName}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {ATTACHMENT_TYPE_LABELS[attachment.attachmentType] || attachment.attachmentType}
                    {attachment.fileSize && ` • ${formatFileSize(attachment.fileSize)}`}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(attachment)}
                disabled={downloading === attachment.id}
                className="print:hidden"
              >
                {downloading === attachment.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Descargar
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Images Gallery Section
// =============================================================================

/**
 * Wrapper component for patient portal authenticated images.
 * Builds the URL and uses the shared AuthenticatedImage component.
 */
function PatientInstanceImage({
  studyId,
  instanceId,
  size,
  className,
  alt,
  onClick,
}: {
  studyId: string
  instanceId: string
  size: 'thumbnail' | 'full'
  className?: string
  alt: string
  onClick?: () => void
}) {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
  const endpoint = size === 'thumbnail'
    ? `/patient-portal/studies/${studyId}/instances/${instanceId}/thumbnail`
    : `/patient-portal/studies/${studyId}/instances/${instanceId}/image?width=1200&height=1200`

  return (
    <AuthenticatedImage
      src={`${baseUrl}${endpoint}`}
      alt={alt}
      getToken={() => patientTokenStorage.getAccessToken()}
      className={className}
      onClick={onClick}
      loadingComponent={
        <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      }
      errorComponent={
        <div className={`${className} bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center`}>
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      }
    />
  )
}

function ImagesSection({ studyId }: { studyId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-study-instances', studyId],
    queryFn: () => getMyStudyInstances(studyId),
  })

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [descargandoExamen, setDescargandoExamen] = useState<FormatoDeDescarga | null>(null)
  const [fullImageSrc, setFullImageSrc] = useState<string | null>(null)
  const [loadingFull, setLoadingFull] = useState(false)

  const instances = data?.instances || []
  const token = patientTokenStorage.getAccessToken()
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'

  const selectedImage = selectedIndex !== null ? instances[selectedIndex] : null

  const loadFullImage = async (instance: PatientStudyInstance) => {
    setLoadingFull(true)
    try {
      const response = await fetch(
        `${baseUrl}/patient-portal/studies/${studyId}/instances/${instance.id}/image?width=1200&height=1200`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      if (!response.ok) throw new Error('Failed to load image')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      setFullImageSrc(url)
    } catch (err) {
      console.error('Error loading full image:', err)
    } finally {
      setLoadingFull(false)
    }
  }

  const openLightbox = (index: number) => {
    setSelectedIndex(index)
    setFullImageSrc(null)
    loadFullImage(instances[index])
  }

  const closeLightbox = () => {
    setSelectedIndex(null)
    if (fullImageSrc) URL.revokeObjectURL(fullImageSrc)
    setFullImageSrc(null)
  }

  const goToPrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      const newIndex = selectedIndex - 1
      setSelectedIndex(newIndex)
      setFullImageSrc(null)
      loadFullImage(instances[newIndex])
    }
  }

  const goToNext = () => {
    if (selectedIndex !== null && selectedIndex < instances.length - 1) {
      const newIndex = selectedIndex + 1
      setSelectedIndex(newIndex)
      setFullImageSrc(null)
      loadFullImage(instances[newIndex])
    }
  }

  const handleDownload = async (instance: PatientStudyInstance) => {
    setDownloading(instance.id)
    try {
      const response = await fetch(
        `${baseUrl}/patient-portal/studies/${studyId}/instances/${instance.id}/image?format=png&width=2048&height=2048`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      // Sin esta comprobación un error del servidor se guardaba igual: el
      // navegador bajaba el JSON del error con nombre de imagen, y el
      // paciente se quedaba con un archivo que no abre y sin saber por qué.
      if (!response.ok) {
        throw new Error(`El servidor respondió ${response.status}`)
      }
      const blob = await response.blob()
      guardarArchivo(blob, `imagen_${instance.instanceNumber || instance.id}.png`)
    } catch (error) {
      console.error('Error downloading image:', error)
      toast.error('No se pudo descargar la imagen. Inténtelo de nuevo.')
    } finally {
      setDownloading(null)
    }
  }

  /**
   * Descarga el examen completo. Es lo que el paciente necesita de verdad:
   * llevarse todas sus imágenes juntas, no una por una.
   */
  const descargarExamen = async (formato: FormatoDeDescarga) => {
    setDescargandoExamen(formato)
    try {
      const { blob, nombre } = await descargarMiEstudio(studyId, formato)
      guardarArchivo(blob, nombre)
    } catch (error) {
      console.error('Error descargando el examen:', error)
      toast.error('No se pudo descargar el examen. Inténtelo de nuevo.')
    } finally {
      setDescargandoExamen(null)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-teal-600 dark:text-teal-400" />
            <span className="text-gray-500 dark:text-gray-400">Cargando imágenes...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (instances.length === 0) {
    return null
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Imágenes del Estudio
            </CardTitle>
            <CardDescription>
              {instances.length} imagen{instances.length !== 1 ? 'es' : ''} disponible{instances.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0">
            {/* La galería sirve para echar un vistazo; el visor es donde se
                puede acercar y ajustar el brillo, que es lo que hace falta para
                ver de verdad una radiografía en una pantalla cualquiera. */}
            <Link to={APP_ROUTES.PATIENT_PORTAL.VIEWER(studyId)}>
              <Button variant="outline" size="sm">
                <Maximize2 className="mr-2 h-4 w-4" />
                Abrir en el visor
              </Button>
            </Link>

            {/* Descargar el examen entero. DICOM primero y como opción
                principal: es el que le sirve al médico que va a comparar. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => descargarExamen('dicom')}
              disabled={descargandoExamen !== null}
            >
              {descargandoExamen === 'dicom' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Descargar para mi médico (DICOM)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => descargarExamen('png')}
              disabled={descargandoExamen !== null}
            >
              {descargandoExamen === 'png' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Descargar como imágenes (PNG)
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {instances.map((instance, index) => (
              <div
                key={instance.id}
                className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-800 hover:border-teal-400 cursor-pointer transition-all group"
                onClick={() => openLightbox(index)}
              >
                <PatientInstanceImage
                  studyId={studyId}
                  instanceId={instance.id}
                  size="thumbnail"
                  className="w-full h-full object-cover"
                  alt={`Imagen ${instance.instanceNumber || ''}`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center print:hidden">
                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {instance.instanceNumber && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                    #{instance.instanceNumber}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lightbox Modal - Hidden during print */}
      {selectedIndex !== null && selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center print:hidden">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navigation */}
          {selectedIndex > 0 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 rounded-full bg-black/50"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}
          {selectedIndex < instances.length - 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 rounded-full bg-black/50"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image */}
          <div className="max-w-[90vw] max-h-[80vh] relative flex items-center justify-center">
            {loadingFull ? (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-white" />
                <p className="text-white">Cargando imagen...</p>
              </div>
            ) : fullImageSrc ? (
              <img
                src={fullImageSrc}
                alt={`Imagen ${selectedImage.instanceNumber || ''}`}
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <div className="flex items-center justify-center w-64 h-64 bg-gray-800 rounded-lg">
                <ImageIcon className="h-16 w-16 text-gray-600 dark:text-gray-400" />
              </div>
            )}
          </div>

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4 flex items-center justify-between">
            <div>
              <p className="font-medium">
                Imagen {selectedIndex + 1} de {instances.length}
              </p>
              {selectedImage.seriesDescription && (
                <p className="text-sm text-gray-300">{selectedImage.seriesDescription}</p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload(selectedImage)}
              disabled={downloading === selectedImage.id}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              {downloading === selectedImage.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PNG
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function PatientStudyDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data: study, isLoading, error } = useQuery({
    queryKey: ['patient-study', id],
    queryFn: () => getMyStudyById(id!),
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600 dark:text-teal-400" />
          <p className="text-gray-500 dark:text-gray-400">Cargando estudio...</p>
        </div>
      </div>
    )
  }

  if (error || !study) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Estudio no encontrado</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2">No se pudo cargar la información del estudio.</p>
          <Link to={APP_ROUTES.PATIENT_PORTAL.HOME}>
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a mis exámenes
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // Solo el informe decide si hay resultado. `study.status` es el estado del
  // ESTUDIO dentro del flujo de la clinica y no dice si el radiologo firmo:
  // exigirlo aqui hacia que el listado dijera "Resultado listo" y esta misma
  // pantalla, del mismo estudio, dijera "En proceso".
  const hasReport = !!study.reportId

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Back Button */}
      <div className="print:hidden">
        <Link to={APP_ROUTES.PATIENT_PORTAL.HOME}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a mis exámenes
          </Button>
        </Link>
      </div>

      {/* Study Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0">
                <FileImage className="h-7 w-7 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {study.studyDescription || MODALITY_LABELS[study.modality as Modality] || study.modality}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {format(new Date(study.studyDate), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                </p>
              </div>
            </div>
            <PatientStatusBadge hasReport={hasReport} />
          </div>
        </CardContent>
      </Card>

      {/* Study Details */}
      <Card>
        <CardHeader>
          <CardTitle>Detalles del Estudio</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                <FileImage className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Modalidad</p>
                <p className="font-medium">{MODALITY_LABELS[study.modality as Modality] || study.modality}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Fecha del Estudio</p>
                <p className="font-medium">
                  {format(new Date(study.studyDate), "d 'de' MMMM, yyyy", { locale: es })}
                  {study.studyTime && ` - ${study.studyTime}`}
                </p>
              </div>
            </div>

            {study.bodyPartExamined && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Zona Examinada</p>
                  <p className="font-medium">{study.bodyPartExamined}</p>
                </div>
              </div>
            )}

            {study.referringPhysician && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Médico Remitente</p>
                  <p className="font-medium">{study.referringPhysician}</p>
                </div>
              </div>
            )}

            {study.performingPhysician && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Médico Realizador</p>
                  <p className="font-medium">{study.performingPhysician}</p>
                </div>
              </div>
            )}

            {study.institutionName && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <Building className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Institución</p>
                  <p className="font-medium">{study.institutionName}</p>
                </div>
              </div>
            )}

            {study.accessionNumber && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/40 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Número de Acceso</p>
                  <p className="font-medium font-mono">{study.accessionNumber}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Images Section */}
      <ImagesSection studyId={study.id} />

      {/* Attachments Section */}
      <AttachmentsSection studyId={study.id} />

      {/* Report Section */}
      <ReportSection studyId={study.id} hasReport={hasReport} />
    </div>
  )
}
